import { Box, Heading, Image, List, ListItem, Stack } from '@chakra-ui/react';
import { Field, FormSchema, Group } from 'api/types';
import { get, intersection, isBoolean, keyBy, map, pick, property } from 'lodash';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { displayRow, renderValue } from '../../helpers/general';

interface IServiceRecordDataProps {
  recordData: object;
  schema: FormSchema;
  isLoading?: boolean;
}

// groups of 4 position fields (front/rear, left/right) sharing one heading instead of repeating
// it on each field's own label (matches the headings in lang/*/dsb_form.php's op_fields)
const positionFieldGroups = [
  {
    heading: 'Profiltiefe',
    fields: ['tire_tread_depth_front_left', 'tire_tread_depth_front_right', 'tire_tread_depth_rear_left', 'tire_tread_depth_rear_right'],
  },
  {
    heading: 'Reifendruck',
    fields: ['tire_pressure_front_left', 'tire_pressure_front_right', 'tire_pressure_rear_left', 'tire_pressure_rear_right'],
  },
  {
    heading: 'Bremsen prüfen/ Bremsbeläge',
    fields: ['brake_pad_thickness_front_left', 'brake_pad_thickness_front_right', 'brake_pad_thickness_rear_left', 'brake_pad_thickness_rear_right'],
  },
] as const;

function flattenArray(arr: any) {
  const result: any[] = [];
  arr.forEach((obj: any) => {
    result.push(obj);
    if (obj.children && obj.children.length > 0) {
      obj.children.forEach((childObj: any) => {
        result.push(childObj);
        if (childObj.children && childObj.children.length > 0) {
          result.push(...flattenArray(childObj.children));
        }
      });
    }
  });
  return result;
}

export const ServiceRecordData: FC<IServiceRecordDataProps> = (props) => {
  const { recordData, schema, isLoading = false } = props;
  const { t } = useTranslation();

  //todo config differently when we use sections
  const groups = get(schema, 'sections[0].groups', []);

  return (
    <>
      {groups.map((group: Group, i) => {
        const overlap = intersection(Object.keys(recordData), map(group.fields, property('name')));
        if (group.fields.length > 0 && overlap.length > 0 && Object.values(pick(recordData, overlap)).some((x) => x)) {
          const fields = flattenArray(group.fields);
          const fieldsByName = keyBy(fields, 'name');
          return (
            <Box key={i}>
              <Stack direction={'row'} alignItems={'center'} spacing={3}>
                <Image ml={2} boxSize={10} src={group?.icon} />,
                <Heading fontSize="xxs" lineHeight={1}>
                  {get(group, 'label')}
                </Heading>
              </Stack>
              <List ml="60px" sx={{ '& > li:not(:first-of-type)': { pt: 2 } }}>
                {fields.map((field: Field, i) => {
                  const fieldKey = field?.name;

                  const positionGroup = positionFieldGroups.find((g) => (g.fields as readonly string[]).includes(fieldKey));
                  if (positionGroup) {
                    // only render once, on the group's first field; the other 3 are skipped here
                    if (fieldKey !== positionGroup.fields[0]) {
                      return null;
                    }
                    // @ts-expect-error
                    if (!positionGroup.fields.some((name) => recordData[name])) {
                      return null;
                    }
                    return (
                      <Fragment key={i}>
                        <ListItem mt="2">{displayRow({ label: positionGroup.heading, content: null, isLoading })}</ListItem>
                        <List ml="6" sx={{ '& > li:not(:first-of-type)': { pt: 1 } }}>
                          {positionGroup.fields.map((name) => {
                            const groupField = fieldsByName[name];
                            return (
                              <ListItem key={name}>
                                {displayRow({
                                  label: groupField?.label,
                                  // @ts-expect-error
                                  content: renderValue(recordData[name], groupField),
                                  isLoading,
                                })}
                              </ListItem>
                            );
                          })}
                        </List>
                      </Fragment>
                    );
                  }

                  const contentOnNewLine = field.display.type === 'string';

                  if (fieldKey === 'ford_service_special_checked') {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: get(field, 'label'),
                          content:
                            // @ts-expect-error
                            recordData['ford_service_special_checked'] === 'not_done'
                              ? 'Offene Service-Aktionen nicht durchgeführt'
                              : // @ts-expect-error
                                recordData['ford_service_special_checked'] === 'done'
                                ? 'Offene Service-Aktionen durchgeführt'
                                : 'Keine ausstehenden Service-Aktionen',
                          // @ts-expect-error
                          noColon: isBoolean(recordData[fieldKey]),
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }

                  if (recordData[fieldKey as keyof typeof recordData] === true && field?.labels?.length) {
                    return (
                      <Fragment key={i}>
                        {field.labels.map((label, labelIndex) => (
                          <ListItem key={`${i}-${labelIndex}`}>
                            {displayRow({
                              label,
                              content: t('common:yes'),
                              isLoading,
                              contentOnNewLine,
                            })}
                          </ListItem>
                        ))}
                      </Fragment>
                    );
                  }

                  if (
                    // @ts-expect-error
                    recordData[fieldKey] &&
                    fieldKey !== 'oil_viscosity_high_temp' &&
                    fieldKey !== 'oil_viscosity_low_temp'
                  ) {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: get(field, 'label'),
                          content:
                            // a ticked box reads as "Yes" whether or not the field is display_always,
                            // so the list does not mix rows that have a value with rows that do not
                            // @ts-expect-error
                            recordData[fieldKey] === true
                              ? t('common:yes')
                              : // @ts-expect-error
                                recordData[fieldKey] && recordData[fieldKey] !== true
                                ? // @ts-expect-error
                                  ` ${renderValue(recordData[fieldKey], field)}`
                                : null,
                          // @ts-expect-error
                          noColon: isBoolean(recordData[fieldKey]),
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }

                  if (
                    fieldKey === 'oil_viscosity_low_temp' &&
                    // @ts-expect-error
                    recordData['oil_viscosity_low_temp'] !== null &&
                    // @ts-expect-error
                    recordData['oil_viscosity_low_temp'] !== undefined &&
                    // @ts-expect-error
                    recordData['oil_viscosity_high_temp'] !== null &&
                    // @ts-expect-error
                    recordData['oil_viscosity_high_temp'] !== undefined
                  ) {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: t('forms:oil_viscosity.label'), // @ts-expect-error
                          content: `${recordData['oil_viscosity_low_temp']}W${recordData['oil_viscosity_high_temp']}`,
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }
                  if (fieldKey === 'oil_viscosity_high_temp') {
                    return null;
                  }

                  if (
                    fieldKey === 'daimler_remaining_time' &&
                    // @ts-expect-error
                    recordData['daimler_remaining_time'] !== undefined
                  ) {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: get(field, 'label'),
                          // @ts-expect-error
                          content: recordData['daimler_remaining_time'],
                          // @ts-expect-error
                          noColon: isBoolean(recordData[fieldKey]),
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }
                  if (
                    fieldKey === 'daimler_remaining_mileage' &&
                    // @ts-expect-error
                    recordData['daimler_remaining_mileage'] !== undefined
                  ) {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: get(field, 'label'),
                          // @ts-expect-error
                          content: recordData['daimler_remaining_mileage'],
                          // @ts-expect-error
                          noColon: isBoolean(recordData[fieldKey]),
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }
                  // @ts-expect-error
                  if (recordData[fieldKey] === false && field?.display?.display_always) {
                    return (
                      <ListItem key={i}>
                        {displayRow({
                          label: get(field, 'label'),
                          content: field?.display?.display_always ? t('common:no') : null,
                          // @ts-expect-error
                          noColon: isBoolean(recordData[fieldKey]),
                          isLoading,
                          contentOnNewLine,
                        })}
                      </ListItem>
                    );
                  }
                  return null;
                })}
              </List>
            </Box>
          );
        }
        return null;
      })}
    </>
  );
};

export default ServiceRecordData;
